import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { logoutAndClearCaches } from '../../../models/UiUtils';
import { UserSessionContext } from '../../../viewmodels/AppViewModels';
import { Button } from '../../components-v2/Button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { logoutFromServer } from './logoutFromServer';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	isAida?: boolean;
	userSession?: UserSessionContext;
	textMessaging?: Api.TextMessagingViewModel;
}

const DefaultFailureMessage = 'We could not fully sign you out. Please try again.';

const clearLocalSession = () => {
	try {
		logoutAndClearCaches();
	} catch (error) {
		console.error('Failed to clear local state during logout:', error);
	}
};

const _Logout: React.FC<IProps> = ({ history, isAida, userSession }) => {
	// A session the server still honours would sign straight back in on the next load, so local state
	// is only cleared once the server has ended it; until then the user can only retry.
	const { error, isError, isIdle, mutate } = useMutation<void, Api.IOperationResultNoValue>({
		mutationFn: async () => {
			const result = await logoutFromServer(userSession);
			if (!result.success) {
				throw result;
			}
		},
		onError: failure => toast.error(failure?.systemMessage || DefaultFailureMessage),
		onSuccess: () => {
			// Leave /logout before clearing the session: its route guard would otherwise see a signed-out user,
			// redirect to /login remembering /logout, and the next sign-in would land back here and sign out again.
			history.push({ pathname: '/login', state: { isAida } });
			clearLocalSession();
		},
	});

	React.useEffect(() => {
		if (isIdle) {
			mutate();
		}
	}, [isIdle, mutate]);

	if (!isError) {
		return <LoadingSpinner type='login' />;
	}

	return (
		<div className={css(styleSheet.container)}>
			<p className={css(styleSheet.message)}>{error?.systemMessage || DefaultFailureMessage}</p>
			<Button onClick={() => mutate()}>Try again</Button>
		</div>
	);
};

export const Logout = withRouter(_Logout);
